import React from "react";

/** Match canonical URL construction used in `components/seo.js`. */
export function absoluteCanonicalUrl(siteUrl, slug) {
  const base = (siteUrl || "https://www.tomedes.com").replace(/\/$/, "");
  const path =
    slug && slug.endsWith("/") ? slug.slice(0, -1) : slug || "";
  return `${base}${path}`;
}

/**
 * Visible How-to block (must stay in sync with `buildToolHowToLdJson` step text/name).
 */
export function ToolHowToSection({ sectionId, heading, description, steps }) {
  return (
    <section id={sectionId} className="bg-white py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="text-[#0A2641] font-bold font-primary text-[19px] sm:text-[28px] leading-[33.82px] mb-4">
          {heading}
        </h2>
        <p className="text-[#424242] font-opensans text-base leading-6 mb-8">
          {description}
        </p>
        <ol className="list-decimal pl-6 sm:pl-8 space-y-4 text-[#424242] font-opensans text-base leading-6 [&>li]:pl-2">
          {steps.map(({ id, lead, body }) => (
            <li key={id} id={id}>
              <strong>{lead}</strong> {body}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/** Plain text for JSON-LD Answer fields (strip HTML / line breaks from CMS or JSON copy). */
export function stripHtmlForSchemaMarkup(html) {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Build FAQPage schema from tool JSON `toolFaq_data` when a prebuilt locale schema is absent.
 */
export function buildFaqPageSchemaFromToolFaq(pageBaseUrl, toolFaqData, inLanguage) {
  if (!pageBaseUrl || !Array.isArray(toolFaqData) || toolFaqData.length === 0) {
    return undefined;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${pageBaseUrl}#faq`,
    inLanguage: inLanguage || "en",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageBaseUrl,
    },
    mainEntity: toolFaqData.map((item) => ({
      "@type": "Question",
      name: item.title,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtmlForSchemaMarkup(item.description),
      },
    })),
  };
}

export function buildToolHowToLdJson(pageBaseUrl, { name, description, toolName, steps }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "@id": `${pageBaseUrl}#howto`,
    name,
    description,
    totalTime: "PT1M",
    tool: [
      {
        "@type": "HowToTool",
        name: toolName,
      },
    ],
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: `${pageBaseUrl}#step-${i + 1}`,
    })),
  };
}
